import { escapeRegExp } from 'lodash';

// A regex alternation of the tokens, longest-first so a token that is a prefix of another doesn't win. A token that
// `isWord` says can be an ordinary word only matches on its own, not inside a longer word.
export const tokenAlternation = (tokens: string[], isWord: (token: string) => boolean = () => false) =>
    [...tokens]
        .sort((a, b) => b.length - a.length)
        .map((token) =>
            isWord(token) ? `(?<![A-Za-z0-9_])${escapeRegExp(token)}(?![A-Za-z0-9_])` : escapeRegExp(token)
        )
        .join('|');

// DOM helpers for contentEditable prompt editors that render known tokens as non-editable chips.
// `tokenAttr` is the attribute a chip stores its token on; everything else in the editor is literal text.
export const createChipEditor = (tokenAttr: string, isWord?: (token: string) => boolean) => {
    const createChip = (token: string, className: string): HTMLSpanElement => {
        const chip = document.createElement('span');
        chip.setAttribute(tokenAttr, token);
        chip.contentEditable = 'false';
        chip.className = className;
        chip.textContent = token;
        return chip;
    };

    const buildNodes = (text: string, classByToken: Map<string, string>): Node[] => {
        if (classByToken.size === 0) return [document.createTextNode(text)];
        const alternation = tokenAlternation([...classByToken.keys()], isWord);
        return text.split(new RegExp(`(${alternation})`, 'g')).map((part) => {
            const className = classByToken.get(part);
            return className ? createChip(part, className) : document.createTextNode(part);
        });
    };

    const serializeToText = (container: HTMLElement): string => {
        let text = '';
        container.childNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent || '';
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                const token = el.getAttribute(tokenAttr);
                if (token) {
                    text += token;
                } else if (el.tagName === 'BR') {
                    text += '\n';
                } else if (el.tagName === 'DIV' || el.tagName === 'P') {
                    if (text.length > 0 && !text.endsWith('\n')) text += '\n';
                    text += serializeToText(el);
                } else {
                    text += serializeToText(el);
                }
            }
        });
        return text;
    };

    return { createChip, buildNodes, serializeToText };
};
